'use client'

import { Check } from 'lucide-react'
import {
  getInterviewPracticeCurrency,
  getInterviewPracticeDisplayPrice,
  type InterviewPracticePlanType,
  type ProductPack,
} from '@/lib/productPacks'
import { ENABLE_SYSTEM_DESIGN_INTERVIEW } from '@/lib/config'
import { formatLandingPrice, showLandingGstNote } from '@/lib/formatLandingPrice'
import styles from './interview-practice-campaign.module.css'

type PackCard = {
  planType: InterviewPracticePlanType
  name: string
  badge: string
  blurb: string
  features: string[]
  featured?: boolean
  comingSoon?: boolean
}

const PACK_CARDS: PackCard[] = [
  {
    planType: 'skill_assessment',
    name: 'Mock Interview',
    badge: 'Quick start',
    blurb: 'A resume-based round that mirrors the real thing.',
    features: [
      'CV-based personalized questions',
      'Real interview-like experience',
      'Detailed performance feedback',
      'Improve confidence & communication',
      'Quick, on-demand practice',
    ],
  },
  {
    planType: 'jd_prep',
    name: 'JD Interview',
    badge: 'Most popular',
    blurb: 'Questioned against the role you are actually chasing.',
    features: [
      'Questions tailored to the job description',
      'Role-specific skill evaluation',
      'Real-world interview scenarios',
      'Actionable feedback for improvement',
      'Align preparation with expectations',
    ],
    featured: true,
  },
  {
    planType: 'system_design',
    name: 'System Design',
    badge: 'Senior track',
    blurb: 'Design, defend and get critiqued on real problems.',
    features: [
      'Real-world system design problems',
      'Scalability & architecture thinking',
      'Explain and defend your approach',
      'Structured feedback on decisions',
      'Ideal for senior-level interviews',
    ],
    comingSoon: !ENABLE_SYSTEM_DESIGN_INTERVIEW,
  },
]

type PricingDialogProps = {
  packs: ProductPack[]
  loading: boolean
  onSelect: (pack: ProductPack) => void
  onClose: () => void
}

/**
 * Three-packs-at-a-glance pricing dialog.
 *
 * Deliberately not the landing page's pricing section: that one is a long scroll with chips and
 * add-ons, and it cannot be made to sit in a rectangle with equal cards. The prices here still come
 * from the same helpers (`getInterviewPracticeDisplayPrice` + `formatLandingPrice`), so they cannot
 * disagree with the landing page.
 */
export function PricingDialog({ packs, loading, onSelect, onClose }: PricingDialogProps) {
  const currency = getInterviewPracticeCurrency(packs)
  const showGst = showLandingGstNote(currency)

  return (
    <div className={styles.pricingDialogBackdrop} role="presentation" onClick={onClose}>
      <div
        className={styles.pricingDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-pricing-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={styles.pricingDialogClose}
          onClick={onClose}
          aria-label="Close pricing"
          autoFocus
        >
          <span aria-hidden="true">×</span>
        </button>

        <header className={styles.pricingDialogHeader}>
          <h2 id="campaign-pricing-title" className={styles.pricingDialogTitle}>
            Choose your interview pack
          </h2>
          <p className={styles.pricingDialogSubtitle}>
            One realistic round is enough to find the gaps before the panel does.
          </p>
        </header>

        {loading ? (
          <p className={styles.pricingDialogState}>Loading plans…</p>
        ) : packs.length === 0 ? (
          <p className={styles.pricingDialogState}>
            Plans are unavailable right now. Please try again in a moment.
          </p>
        ) : (
          <div className={styles.packGrid}>
            {PACK_CARDS.map((card) => {
              const pack = packs.find((item) => item.payment_plan_type === card.planType) ?? null
              const amount = getInterviewPracticeDisplayPrice(packs, card.planType)
              const original = pack?.original_price ?? null
              const discounted = original != null && amount != null && original > amount
              const disabled = card.comingSoon || !pack

              return (
                <article
                  key={card.planType}
                  className={`${styles.packCard} ${card.featured ? styles.packCardFeatured : ''}`}
                >
                  <span
                    className={`${styles.packBadge} ${card.featured ? styles.packBadgeFeatured : ''}`}
                  >
                    {card.badge}
                  </span>

                  <h3 className={styles.packName}>{card.name}</h3>
                  <p className={styles.packBlurb}>{card.blurb}</p>

                  <p className={styles.packPriceRow}>
                    <span className={styles.packPrice}>
                      {amount == null ? '—' : formatLandingPrice(amount, currency)}
                    </span>
                    {discounted ? (
                      <span className={styles.packOriginal}>
                        {formatLandingPrice(original, currency)}
                      </span>
                    ) : null}
                    {showGst ? <span className={styles.packGst}>+GST</span> : null}
                  </p>

                  <ul className={styles.packFeatures}>
                    {card.features.map((feature) => (
                      <li key={feature} className={styles.packFeature}>
                        <span className={styles.packFeatureIcon} aria-hidden="true">
                          <Check size={11} strokeWidth={3} />
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    className={`${styles.packCta} ${card.featured ? styles.packCtaFeatured : ''}`}
                    disabled={disabled}
                    onClick={() => (pack ? onSelect(pack) : undefined)}
                  >
                    {card.comingSoon ? 'Coming soon' : 'Start practicing'}
                  </button>
                </article>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
